import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack, IconButton, InputAdornment, List, ListItem, ListItemText, ListItemSecondaryAction, Divider, Switch, CircularProgress, Alert,
  MenuItem, Select, FormControl, FormLabel, Tooltip, DialogContentText, Table, TableBody, TableCell, TableHead, TableRow, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  getAutomationRules, createAutomationRule, updateAutomationRule, deleteAutomationRule, getAutomationRuleHistory, testAutomationRule,
} from '../../api/automation.api.js';

const TRIGGER_TYPES = [
  { value: 'event', label: 'Event', configFields: ['event'] },
  { value: 'schedule', label: 'Schedule', configFields: ['cron'] },
  { value: 'webhook', label: 'Webhook', configFields: ['url'] },
  { value: 'field_change', label: 'Field Change', configFields: ['field'] },
];
const ACTION_TYPES = [
  { value: 'run_flow', label: 'Run Flow', configFields: ['flowId'] },
  { value: 'send_email', label: 'Send Email', configFields: ['to', 'subject', 'body'] },
  { value: 'webhook', label: 'Webhook', configFields: ['url', 'payload'] },
  { value: 'update_record', label: 'Update Record', configFields: ['collection', 'recordId', 'fields'] },
];

function AutomationRules() {
  const [rules, setRules] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [testResult, setTestResult] = useState(null);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    enabled: true,
    triggers: [{ type: 'event', config: { event: '' } }],
    actions: [{ type: 'run_flow', config: { flowId: '' } }],
    conditions: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRules = async () => {
    setLoading(true); setError(null);
    try {
      const data = await getAutomationRules();
      setRules(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRules(); }, []);

  const filteredRules = rules.filter(
    (r) => r.name.toLowerCase().includes(search.toLowerCase())
      || r.description.toLowerCase().includes(search.toLowerCase()),
  );

  const handleOpenDialog = (rule = null) => {
    setEditRule(rule);
    setForm(rule ? {
      name: rule.name,
      description: rule.description,
      enabled: rule.enabled,
      triggers: rule.triggers?.length ? rule.triggers : [{ type: 'event', config: { event: '' } }],
      actions: rule.actions?.length ? rule.actions : [{ type: 'run_flow', config: { flowId: '' } }],
      conditions: rule.conditions || [],
    } : {
      name: '',
      description: '',
      enabled: true,
      triggers: [{ type: 'event', config: { event: '' } }],
      actions: [{ type: 'run_flow', config: { flowId: '' } }],
      conditions: [],
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditRule(null);
    setForm({
      name: '', description: '', enabled: true, triggers: [{ type: 'event', config: { event: '' } }], actions: [{ type: 'run_flow', config: { flowId: '' } }], conditions: [],
    });
  };

  const handleSave = async () => {
    setLoading(true); setError(null);
    try {
      if (editRule) {
        const updated = await updateAutomationRule(editRule._id || editRule.id, form);
        setRules((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
      } else {
        const created = await createAutomationRule(form);
        setRules((prev) => [created, ...prev]);
      }
      handleCloseDialog();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true); setError(null);
    try {
      await deleteAutomationRule(id);
      setRules((prev) => prev.filter((r) => r._id !== id && r.id !== id));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewHistory = async (ruleId) => {
    setLoading(true); setError(null);
    try {
      const h = await getAutomationRuleHistory(ruleId);
      setHistory(h);
      setHistoryDialogOpen(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  const handleTestRule = async (ruleId) => {
    setLoading(true); setError(null);
    try {
      const result = await testAutomationRule(ruleId);
      setTestResult(result);
      setTestDialogOpen(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Automation Rules</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          New Rule
        </Button>
      </Stack>
      <TextField
        placeholder="Search rules..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />
      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <List>
        {filteredRules.map((rule) => (
          <React.Fragment key={rule._id || rule.id}>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={rule.name}
                secondary={(
                  <>
                    {rule.description}
                    <br />
                    <span style={{ color: '#888' }}>
                      Triggers:
                      {' '}
                      {rule.triggers?.map((t) => t.type).join(', ') || 'N/A'}
                      {' '}
                      | Actions:
                      {' '}
                      {rule.actions?.map((a) => a.type).join(', ')}
                    </span>
                  </>
                )}
              />
              <Switch
                checked={rule.enabled}
                onChange={async () => {
                  setLoading(true); setError(null);
                  try {
                    const updated = await updateAutomationRule(rule._id || rule.id, { ...rule, enabled: !rule.enabled });
                    setRules((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
                  } catch (err) {
                    setError(err.message);
                  } finally {
                    setLoading(false);
                  }
                }}
                color="primary"
                inputProps={{ 'aria-label': 'Enable/disable rule' }}
              />
              <ListItemSecondaryAction>
                <Tooltip title="Edit"><IconButton edge="end" onClick={() => handleOpenDialog(rule)}><EditIcon /></IconButton></Tooltip>
                <Tooltip title="Delete"><IconButton edge="end" color="error" onClick={() => handleDelete(rule._id || rule.id)}><DeleteIcon /></IconButton></Tooltip>
                <Tooltip title="View History"><IconButton edge="end" onClick={() => handleViewHistory(rule._id || rule.id)}><span className="material-icons">history</span></IconButton></Tooltip>
                <Tooltip title="Test Rule"><IconButton edge="end" onClick={() => handleTestRule(rule._id || rule.id)}><span className="material-icons">play_arrow</span></IconButton></Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
        {filteredRules.length === 0 && !loading && (
          <ListItem>
            <ListItemText primary="No rules found." />
          </ListItem>
        )}
      </List>
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editRule ? 'Edit Rule' : 'New Rule'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            fullWidth
            margin="normal"
            multiline
            minRows={2}
          />
          {/* Triggers Builder */}
          <Box mt={2} mb={2}>
            <Typography variant="subtitle1">Triggers</Typography>
            {form.triggers.map((trigger, idx) => (
              <Stack direction="row" spacing={2} alignItems="center" key={idx} mb={1}>
                <FormControl sx={{ minWidth: 120 }}>
                  <FormLabel>Type</FormLabel>
                  <Select
                    value={trigger.type}
                    onChange={(e) => {
                      const type = e.target.value;
                      setForm((f) => {
                        const newTriggers = [...f.triggers];
                        newTriggers[idx] = { type, config: {} };
                        return { ...f, triggers: newTriggers };
                      });
                    }}
                  >
                    {TRIGGER_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </Select>
                </FormControl>
                {TRIGGER_TYPES.find((t) => t.value === trigger.type)?.configFields.map((field) => (
                  <TextField
                    key={field}
                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                    value={trigger.config[field] || ''}
                    onChange={(e) => {
                      setForm((f) => {
                        const newTriggers = [...f.triggers];
                        newTriggers[idx] = {
                          ...newTriggers[idx],
                          config: { ...newTriggers[idx].config, [field]: e.target.value },
                        };
                        return { ...f, triggers: newTriggers };
                      });
                    }}
                    sx={{ minWidth: 120 }}
                  />
                ))}
                <Button onClick={() => setForm((f) => ({ ...f, triggers: f.triggers.filter((_, i) => i !== idx) }))} disabled={form.triggers.length === 1}>Remove</Button>
              </Stack>
            ))}
            <Button onClick={() => setForm((f) => ({ ...f, triggers: [...f.triggers, { type: 'event', config: { event: '' } }] }))}>Add Trigger</Button>
          </Box>
          {/* Actions Builder */}
          <Box mt={2} mb={2}>
            <Typography variant="subtitle1">Actions</Typography>
            {form.actions.map((action, idx) => (
              <Stack direction="row" spacing={2} alignItems="center" key={idx} mb={1}>
                <FormControl sx={{ minWidth: 120 }}>
                  <FormLabel>Type</FormLabel>
                  <Select
                    value={action.type}
                    onChange={(e) => {
                      const type = e.target.value;
                      setForm((f) => {
                        const newActions = [...f.actions];
                        newActions[idx] = { type, config: {} };
                        return { ...f, actions: newActions };
                      });
                    }}
                  >
                    {ACTION_TYPES.map((a) => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
                  </Select>
                </FormControl>
                {ACTION_TYPES.find((a) => a.value === action.type)?.configFields.map((field) => (
                  <TextField
                    key={field}
                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                    value={action.config[field] || ''}
                    onChange={(e) => {
                      setForm((f) => {
                        const newActions = [...f.actions];
                        newActions[idx] = {
                          ...newActions[idx],
                          config: { ...newActions[idx].config, [field]: e.target.value },
                        };
                        return { ...f, actions: newActions };
                      });
                    }}
                    sx={{ minWidth: 120 }}
                  />
                ))}
                <Button onClick={() => setForm((f) => ({ ...f, actions: f.actions.filter((_, i) => i !== idx) }))} disabled={form.actions.length === 1}>Remove</Button>
              </Stack>
            ))}
            <Button onClick={() => setForm((f) => ({ ...f, actions: [...f.actions, { type: 'run_flow', config: { flowId: '' } }] }))}>Add Action</Button>
          </Box>
          {/* Conditions Builder */}
          <Box mt={2} mb={2}>
            <Typography variant="subtitle1">Conditions (optional)</Typography>
            {form.conditions.map((cond, idx) => (
              <Stack direction="row" spacing={2} alignItems="center" key={idx} mb={1}>
                <TextField
                  label="Field"
                  value={cond.field || ''}
                  onChange={(e) => setForm((f) => {
                    const newConds = [...f.conditions];
                    newConds[idx] = { ...newConds[idx], field: e.target.value };
                    return { ...f, conditions: newConds };
                  })}
                  sx={{ minWidth: 120 }}
                />
                <TextField
                  label="Operator"
                  value={cond.operator || ''}
                  onChange={(e) => setForm((f) => {
                    const newConds = [...f.conditions];
                    newConds[idx] = { ...newConds[idx], operator: e.target.value };
                    return { ...f, conditions: newConds };
                  })}
                  sx={{ minWidth: 120 }}
                />
                <TextField
                  label="Value"
                  value={cond.value || ''}
                  onChange={(e) => setForm((f) => {
                    const newConds = [...f.conditions];
                    newConds[idx] = { ...newConds[idx], value: e.target.value };
                    return { ...f, conditions: newConds };
                  })}
                  sx={{ minWidth: 120 }}
                />
                <Button onClick={() => setForm((f) => ({ ...f, conditions: f.conditions.filter((_, i) => i !== idx) }))} disabled={form.conditions.length === 0}>Remove</Button>
              </Stack>
            ))}
            <Button onClick={() => setForm((f) => ({ ...f, conditions: [...f.conditions, { field: '', operator: '', value: '' }] }))}>Add Condition</Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {editRule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Rule Execution History</DialogTitle>
        <DialogContent>
          {history.length === 0 ? <DialogContentText>No history found.</DialogContentText> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Executed At</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Result</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((h, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{h.executedAt ? new Date(h.executedAt).toLocaleString() : ''}</TableCell>
                    <TableCell>{h.status}</TableCell>
                    <TableCell>{h.result ? JSON.stringify(h.result) : ''}</TableCell>
                    <TableCell style={{ color: 'red' }}>{h.error || ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Test Result Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Test Rule Result</DialogTitle>
        <DialogContent>
          {testResult ? <DialogContentText>{JSON.stringify(testResult)}</DialogContentText> : <DialogContentText>No result.</DialogContentText>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AutomationRules;
